'use client'

import { H4, P } from '@/components/common/Typo'
import { useTranslate } from '@tolgee/react'
import { Button, cn, Input } from 'oddle-shop-monorepo.design-system'
import React, { useState } from 'react'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { SerializedError } from '@reduxjs/toolkit'

interface NewsletterProps {
  newsletterSignupAction: (params: any) => Promise<
    | {
        data: Local.V4ApiResponse<string, unknown>
        error?: undefined
      }
    | {
        data?: undefined
        error: FetchBaseQueryError | SerializedError
      }
  >
  previewMode: boolean
  settings: {
    bodyText: string
    headingsText: string
    requestSubscriberName: boolean
    successMessageText: string
  }
}

const Newsletter = ({
  newsletterSignupAction,
  previewMode,
  settings,
}: NewsletterProps): JSX.Element => {
  const { t } = useTranslate()
  const [submitSucceed, setSubmitSucceed] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
  })
  const [errors, setErrors] = useState<{
    email?: string
    firstName?: string
    lastName?: string
  }>({})

  const validateEmail = (email: string) => {
    if (!email) return t('Email is required')
    if (!/\S+@\S+\.\S+/.test(email))
      return t('Please enter a valid email address')
    return null
  }

  const validateName = (name: string, fieldName: string) => {
    if (!name.trim()) return t(`${fieldName} is required`)
    if (name.trim().length < 2)
      return t(`${fieldName} must be at least 2 characters`)
    return null
  }

  const validateForm = () => {
    const newErrors: typeof errors = {}

    const emailError = validateEmail(formData.email)
    if (emailError) newErrors.email = emailError

    if (settings.requestSubscriberName) {
      const firstNameError = validateName(formData.firstName, 'First Name')
      if (firstNameError) newErrors.firstName = firstNameError

      const lastNameError = validateName(formData.lastName, 'Last Name')
      if (lastNameError) newErrors.lastName = lastNameError
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) return

    setIsSubmitting(true)

    try {
      if (previewMode) {
        setSubmitSucceed(true)
        return
      }

      const response = await newsletterSignupAction({
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
      })

      if (response?.data?.data?.data) {
        setSubmitSucceed(true)
      } else {
        setErrors({
          email: t('Looks like something went wrong, please try again later.'),
        })
      }
    } catch (error) {
      setErrors({
        email: t('Looks like something went wrong, please try again later.'),
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field as keyof typeof errors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }))
    }
  }

  const { requestSubscriberName, bodyText, successMessageText } = settings

  return (
    <div>
      <H4 className="normalised mb-2 text-center font-bold md:text-left">
        {settings.headingsText}
      </H4>

      {submitSucceed ? (
        <div className="text-center md:text-left">
          <div
            className="text-headings text-center md:text-left"
            dangerouslySetInnerHTML={{ __html: successMessageText }}
          />
        </div>
      ) : (
        <>
          <div className="text-center md:text-left">
            <P
              className="text-center md:text-left"
              dangerouslySetInnerHTML={{ __html: bodyText }}
            />
          </div>

          <form
            onSubmit={handleSubmit}
            className="mt-2 w-full md:max-w-[300px]"
          >
            {requestSubscriberName && (
              <>
                <div className="mb-2 w-full">
                  <Input
                    type="text"
                    placeholder={t('First Name')}
                    value={formData.firstName}
                    onChange={(e) =>
                      handleInputChange('firstName', e.target.value)
                    }
                    className={cn(
                      'w-full rounded-lg bg-white',
                      errors.firstName && 'border-red-500'
                    )}
                  />
                  {errors.firstName && (
                    <P className="mt-1 text-sm text-red-500">
                      {errors.firstName}
                    </P>
                  )}
                </div>
                <div className="mb-2 w-full">
                  <Input
                    type="text"
                    placeholder={t('Last Name')}
                    value={formData.lastName}
                    onChange={(e) =>
                      handleInputChange('lastName', e.target.value)
                    }
                    className={cn(
                      'w-full rounded-lg bg-white',
                      errors.lastName && 'border-red-500'
                    )}
                  />
                  {errors.lastName && (
                    <P className="mt-1 text-sm text-red-500">
                      {errors.lastName}
                    </P>
                  )}
                </div>
              </>
            )}

            <div className="flex">
              <div className="flex-1">
                <Input
                  type="email"
                  placeholder={t('Email')}
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={cn(
                    'w-full rounded-l-lg rounded-r-none bg-white',
                    errors.email && 'border-red-500'
                  )}
                />
                {errors.email && (
                  <P className="mt-1 text-sm text-red-500">{errors.email}</P>
                )}
              </div>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-body rounded-l-none rounded-r-lg px-4 py-2 hover:opacity-90 disabled:opacity-50"
              >
                <P className="text-background normalised m-0 font-bold">
                  {t('Subscribe')}
                </P>
              </Button>
            </div>
          </form>
        </>
      )}
    </div>
  )
}

export default Newsletter
